import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Moment from "moment";
import HelpHint from "../parts/helpHint";
import toast from "../../utils/toast.jsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CollectionsApi, UsersApi } from "../../services/api/api";
import TierDropdown from "../parts/tierDropdown";

function CollectionAdd(props) {
  const { token } = props;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialFolderPath = searchParams.get('folder') ? searchParams.get('folder').split('/') : [];
  const [name, setName] = useState("");
  const [formTier, setFormTier] = useState("0");
  const [defaultTier, setDefaultTier] = useState(
    localStorage.getItem("tour") === "true" ? "1" : null
  );
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("no note");
  const [monthValue, setMonthValue] = useState(new Date()); // Initialize with current date
  const [monthlyEnabled, setMonthlyEnabled] = useState(false);
  const [typeStep, setTypeStep] = useState(0);
  const isMounted = useRef(true);
  const typeWriterTimeout = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    const fetchMonthlySetting = async () => {
      try {
        let authToken = token;
        if (typeof authToken === 'function') {
          authToken = await authToken();
        }
        if (authToken) {
          const res = await UsersApi.getMonthlyDistributionSetting(authToken);
          if (res?.data?.app_metadata?.monthlyDistributionEnabled) {
            setMonthlyEnabled(true);
          }
        }
      } catch (err) {
        // Default to false on error
      }
    };
    fetchMonthlySetting();
    return () => {
      isMounted.current = false;
      if (typeWriterTimeout.current) {
        clearTimeout(typeWriterTimeout.current);
      }
    };
  }, [token]);

  const submitForm = async (ev) => {
    ev.preventDefault();
    if (loading) return;

    if (name.trim() === "") {
      toast.warn("You can't have an empty collection name.", { autoClose: 5000 });
      return;
    }
    setLoading(true);

    try {
      let authToken = token;
      if (typeof authToken === 'function') {
        authToken = await authToken();
      }
      if (!authToken || typeof authToken !== 'string' || authToken === 'undefined') {
        toast.error('No valid token found for collection add API call.');
        setLoading(false);
        return;
      }
      
      // Convert monthValue to Unix timestamp using the same logic as CollectionDetails
      let activeMonth;
      if (monthValue) {
        if (monthValue instanceof Date) {
          const year = monthValue.getFullYear();
          const month = monthValue.getMonth(); // 0-based
          const dateString = `${year}-${String(month + 1).padStart(2, '0')}-15`;
          activeMonth = Moment.utc(dateString, 'YYYY-MM-DD').unix();
        } else {
          // Fallback for unexpected date formats
          activeMonth = Moment(monthValue).unix();
        }
      }
      
      const payload = {
        name: name,
        tier: formTier || "0",
        notes: notes,
      };
      if (initialFolderPath.length > 0) {
        payload.folderPath = initialFolderPath;
      }
      if (activeMonth) {
        payload.activeMonth = activeMonth;
      }
      
      const response = await CollectionsApi.createCollection(authToken, payload);

      if (response.success) {
        toast.success("Collection created successfully!");
        if (initialFolderPath.length > 0) {
          navigate(`/collections/?folder=${encodeURIComponent(initialFolderPath.join('/'))}`);
        } else {
          navigate("/collections/");
        }
      } else {
        toast.error(`Error: ${response.message || "Failed to create collection."}`);
      }
    } catch (err) {
      toast.error("An error occurred while creating the collection.");
      console.error(err);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleTierChange = (color, id, name) => {
    setFormTier(id);
  };

  const typeWriter = (text, setter, i = 0) => {
    if (i < text.length) {
      setter(text.substring(0, i + 1));
      typeWriterTimeout.current = setTimeout(() => typeWriter(text, setter, i + 1), 20);
    }
  };

  return (
    <>
      <Box sx={{ px: 2 }}>
        <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 2 }} id="tour-01">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: "primary.main" }}>
              Add Collection
            </Typography>
            <HelpHint
              title="Add a Collection"
              hints={[
                "Enter a name for your collection",
                "Optionally assign a tier and notes",
              ]}
            />
          </Box>
          <form onSubmit={submitForm}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={8} id="tour-02">
                <TextField
                  label="Collection Name"
                  variant="outlined"
                  fullWidth
                  required
                  inputProps={{ 'data-testid': 'collection-name-input' }}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  sx={{ mb: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={4} id="tour-03">
                <TierDropdown
                  action={handleTierChange}
                  token={token}
                  defaultValue={defaultTier}
                  label="Tier"
                />
              </Grid>
              <Grid item xs={12} sm={8} id="tour-04">
                <TextField
                  label="Notes"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add a Note here... Not required"
                />
              </Grid>
              {monthlyEnabled && (
                <Grid item xs={12} sm={4} id="tour-05">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      label="Active Month"
                      value={monthValue ? Moment(monthValue) : null}
                      onChange={(value) => {
                        if (value && value.isValid && value.isValid()) {
                          const year = value.year();
                          const month = value.month(); // 0-based
                          const selectedDate = new Date(year, month, 15); // Local date for the 15th
                          setMonthValue(selectedDate);
                        } else {
                          setMonthValue(null);
                        }
                      }}
                      views={['month', 'year']}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Grid>
              )}
              <Grid item xs={12} sx={{ pt: 3 }}>
                <Button
                  id="tour-06"
                  type="submit"
                  data-testid="create-collection-button"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                  sx={{ px: 4, py: 1.5 }}
                >
                  {loading ? "Creating Collection..." : "Create Collection"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </>
  );
}

export default CollectionAdd;
